import { FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { OptionsProps } from "../../components";
import { formatDateTimestamp, translate } from "../../intl";
import { OptionsEnum } from "../forms";
import {
  CreationTypeEnum,
  CtoKeyStepEnum,
  CtoStepEnum,
  RiskLevelEnum,
  RiskLevelLabel,
  StepToReturnEnum,
  StepToReturnNewStep6Enum,
  StepToReturnStep8Enum,
} from "../../types/cto";
import { CtoStatus, StepApproval } from "../../types";

type CustomOptions = {
  approve?: string;
  reject?: string;
};

export const getCreationType = (creationType: CreationTypeEnum): string =>
  creationType === CreationTypeEnum.AUTO
    ? translate("ctoStep1.automatic")
    : translate("ctoStep1.manual");

export const formValidate = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(): Omit<
  RegisterOptions<TFieldValues, TName>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
> => {
  return {
    validate: {
      booleanRequired: (value) =>
        value !== undefined && value !== "" && value !== null,
    },
  };
};

export const formMultipleValidate = (rules: any) => {
  return {
    ...rules,
    validate: (value: any[]) => {
      if (rules?.required && !value.length) {
        return typeof rules.required === "string" ? rules.required : false;
      }
      return undefined;
    },
  };
};

export const getValuesRadioButton = (
  formValue: string | boolean | null,
): boolean | null => {
  if (
    formValue === OptionsEnum.UNKNOWN ||
    formValue === OptionsEnum.NOT_APPLICABLE ||
    formValue === OptionsEnum.ON_GOING
  )
    return null;
  if (typeof formValue === "boolean") return formValue;
  return null;
};

export const residualRiskLevel = () => [
  { id: RiskLevelEnum.LOW, label: RiskLevelLabel.LOW },
  { id: RiskLevelEnum.MEDIUM, label: RiskLevelLabel.MEDIUM },
  { id: RiskLevelEnum.HIGH, label: RiskLevelLabel.HIGH },
];

export const getStepToReturnStep7 = (): OptionsProps[] => [
  { label: translate("ctoStep7.step5Label"), value: StepToReturnEnum.STEP_5 },
  { label: translate("ctoStep7.step6Label"), value: StepToReturnEnum.STEP_6 },
];

export const getStepToReturnStep8 = (): OptionsProps[] => [
  {
    label: translate("ctoStep7.step5Label"),
    value: StepToReturnStep8Enum.STEP_5,
  },
  {
    label: translate("ctoStep7.step6Label"),
    value: StepToReturnStep8Enum.STEP_6,
  },
  {
    label: translate("ctoStep7.step7Label"),
    value: StepToReturnStep8Enum.STEP_7,
  },
];

export const getStepToReturnNewStep6 = (): OptionsProps[] => [
  {
    label: translate("newCtoStep6.step4Label"),
    value: StepToReturnNewStep6Enum.STEP_4,
  },
  {
    label: translate("newCtoStep6.step5Label"),
    value: StepToReturnNewStep6Enum.STEP_5,
  },
];

export const getApproveOrRejectOptions = (
  options: CustomOptions = {},
): OptionsProps[] => {
  const {
    approve = translate("generic.approve"),
    reject = translate("generic.reject"),
  } = options;

  return [
    { label: approve, value: true },
    { label: reject, value: false },
  ];
};

export const isEnabledCancelCto = (
  isOpen: boolean,
  currentStep: number,
  creationType: CreationTypeEnum,
): boolean =>
  isOpen &&
  currentStep !== CtoStepEnum.STEP_8 &&
  creationType === CreationTypeEnum.MANUAL;

export const showPenultimateStep = (
  currentStep: number,
  ctoStatus: CtoStatus,
  isNewCto = false,
): number => {
  const penultimateStep = currentStep - 1;
  const isFinalStep =
    currentStep === (isNewCto ? CtoStepEnum.STEP_6 : CtoStepEnum.STEP_8);
  const extensionRequired = ctoStatus === CtoStatus.EXTENSION_REQUIRED;

  if (isFinalStep && extensionRequired) return penultimateStep;

  return currentStep;
};

export const isClosedBeforeCompletion = (
  isOpen: boolean,
  currentStep: number,
): boolean => !isOpen && currentStep === CtoStepEnum.STEP_1;

export const getStepNumberByStepName = (stepName: CtoKeyStepEnum): number =>
  [
    CtoKeyStepEnum.STEP_1,
    CtoKeyStepEnum.STEP_2,
    CtoKeyStepEnum.STEP_3,
    CtoKeyStepEnum.STEP_4,
    CtoKeyStepEnum.STEP_5,
    CtoKeyStepEnum.STEP_6,
    CtoKeyStepEnum.STEP_7,
    CtoKeyStepEnum.STEP_8,
  ].findIndex((item) => item === stepName) + 1;

type ApprovalStatus = "Completed" | "Rejected" | "Pending";

type ApprovalStatusAndColor = {
  position: string;
  status: ApprovalStatus;
  color: string;
  dateTime: string | null;
};

export const getApprovalStatusAndColor = (
  approvals: StepApproval[],
  index: number,
): ApprovalStatusAndColor => {
  const approval = approvals[index];
  if (!approval)
    return {
      position: `Approver ${index + 1}`,
      status: "Pending",
      color: "var(--orange-400)",
      dateTime: null,
    };
  return {
    position:
      approval.step_approved_by?.position ||
      approval.step_approved_by?.name ||
      "-",
    status: approval.approved ? "Completed" : "Rejected",
    color: approval.approved ? "var(--green-400)" : "var(--red-500)",
    dateTime: formatDateTimestamp(approval.step_approved_at),
  };
};
